import { useMemo } from 'react';
import { PriorityLevel, TicketCategory, TicketStatus } from '@sib/api';
import { useTicketsAppTranslation } from '@sib/i18n';

export const useTicketPriorityLabel = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => ({
      [PriorityLevel.Low]: t('enums.ticketPriority.low'),
      [PriorityLevel.Medium]: t('enums.ticketPriority.medium'),
      [PriorityLevel.High]: t('enums.ticketPriority.high'),
    }),
    [t]
  );
};

export const useTicketStatusLabel = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => ({
      [TicketStatus.New]: t('enums.ticketStatus.new'),
      [TicketStatus.InProgress]: t('enums.ticketStatus.inProgress'),
      [TicketStatus.Closed]: t('enums.ticketStatus.closed'),
      [TicketStatus.WorkingWithVendor]: t(
        'enums.ticketStatus.workingWithVendor'
      ),
      [TicketStatus.AwaitingClientAction]: t(
        'enums.ticketStatus.awaitingClientAction'
      ),
      [TicketStatus.AwaitingInternalAction]: t(
        'enums.ticketStatus.awaitingInternalAction'
      ),
      [TicketStatus.Canceled]: t('enums.ticketStatus.canceled'),
      [TicketStatus.Completed]: t('enums.ticketStatus.completed'),
    }),
    [t]
  );
};

export const useTicketStatusOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      { value: TicketStatus.New, label: t('enums.ticketStatus.new') },
      {
        value: TicketStatus.InProgress,
        label: t('enums.ticketStatus.inProgress'),
      },
      { value: TicketStatus.Closed, label: t('enums.ticketStatus.closed') },
      {
        value: TicketStatus.WorkingWithVendor,
        label: t('enums.ticketStatus.workingWithVendor'),
      },
      {
        value: TicketStatus.AwaitingClientAction,
        label: t('enums.ticketStatus.awaitingClientAction'),
      },
      {
        value: TicketStatus.AwaitingInternalAction,
        label: t('enums.ticketStatus.awaitingInternalAction'),
      },
      {
        value: TicketStatus.Canceled,
        label: t('enums.ticketStatus.canceled'),
      },
      {
        value: TicketStatus.Completed,
        label: t('enums.ticketStatus.completed'),
      },
    ],
    [t]
  );
};

export const useTicketTypeOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      {
        value: TicketCategory.Account,
        label: t('enums.ticketType.account'),
      },
      {
        value: TicketCategory.Baselines,
        label: t('enums.ticketType.baselines'),
      },
      {
        value: TicketCategory.Client,
        label: t('enums.ticketType.client'),
      },
      {
        value: TicketCategory.Contract,
        label: t('enums.ticketType.contract'),
      },
      {
        value: TicketCategory.CustomerService,
        label: t('enums.ticketType.customerService'),
      },
      {
        value: TicketCategory.Escalation,
        label: t('enums.ticketType.escalation'),
      },
      {
        value: TicketCategory.Exceptions,
        label: t('enums.ticketType.exceptions'),
      },
      {
        value: TicketCategory.Finance,
        label: t('enums.ticketType.finance'),
      },
      {
        value: TicketCategory.FollowUp,
        label: t('enums.ticketType.followUp'),
      },
      {
        value: TicketCategory.InformationRequest,
        label: t('enums.ticketType.informationRequest'),
      },
      {
        value: TicketCategory.Invoice,
        label: t('enums.ticketType.invoice'),
      },
      {
        value: TicketCategory.MasterData,
        label: t('enums.ticketType.masterData'),
      },
      {
        value: TicketCategory.Negotiation,
        label: t('enums.ticketType.negotiation'),
      },
      {
        value: TicketCategory.Others,
        label: t('enums.ticketType.others'),
      },
      {
        value: TicketCategory.Payments,
        label: t('enums.ticketType.payments'),
      },
      {
        value: TicketCategory.Property,
        label: t('enums.ticketType.property'),
      },
      {
        value: TicketCategory.ServiceRequest,
        label: t('enums.ticketType.serviceRequest'),
      },
      {
        value: TicketCategory.Transitions,
        label: t('enums.ticketType.transitions'),
      },
      {
        value: TicketCategory.VendorLocal,
        label: t('enums.ticketType.vendorLocal'),
      },
      {
        value: TicketCategory.MissingBill,
        label: t('enums.ticketType.missingBill'),
      },
      {
        value: TicketCategory.UpdateMailingAddress,
        label: t('enums.ticketType.updateMailingAddress'),
      },
      {
        value: TicketCategory.PotentialLeak,
        label: t('enums.ticketType.potentialLeak'),
      },
      {
        value: TicketCategory.InvoiceQuestion,
        label: t('enums.ticketType.invoiceQuestion'),
      },
    ],
    [t]
  );
};

export const useTicketPriorityOptions = () => {
  const { t } = useTicketsAppTranslation();

  return useMemo(
    () => [
      { value: PriorityLevel.Low, label: t('enums.ticketPriority.low') },
      {
        value: PriorityLevel.Medium,
        label: t('enums.ticketPriority.medium'),
      },
      { value: PriorityLevel.High, label: t('enums.ticketPriority.high') },
    ],
    [t]
  );
};

export const ticketPriorityColors: Record<PriorityLevel, string> = {
  [PriorityLevel.Low]: '#DAF5FF',
  [PriorityLevel.Medium]: '#FAF2D1',
  [PriorityLevel.High]: '#FFDFEC',
};

export const ticketStatusColors: Record<TicketStatus, string> = {
  [TicketStatus.New]: '#ACEBD3',
  [TicketStatus.InProgress]: '#F7D49C',
  [TicketStatus.Closed]: '#D6D6D6',
  [TicketStatus.WorkingWithVendor]: '#D2DFFD',
  [TicketStatus.AwaitingClientAction]: '#D2DFFD',
  [TicketStatus.AwaitingInternalAction]: '#D2DFFD',
  [TicketStatus.Canceled]: '#FFCCD8',
  [TicketStatus.Completed]: '#ACEBD3',
};
